import { useConnection } from "@solana/wallet-adapter-react";
import { useEffect, useRef, useState } from "react";

/**
 * Returns the on-chain unix timestamp, ticked smoothly via host wall-clock.
 *
 * Surfpool's `Clock::unix_timestamp` drifts ~109 min ahead of `Date.now()` by
 * default (see wiki: surfpool-clock-drift-onchain-now-in-harnesses). Any UI
 * that displays "time left until `expires_at`" computed against `Date.now()`
 * will be wrong by that drift. This hook samples `getBlockTime(slot)` to learn
 * the chain↔host offset, then ticks `Date.now() + offset` between samples for
 * smooth per-second updates without hammering RPC.
 *
 * On devnet/mainnet the offset is ~0 so the hook is harmless.
 */
export function useChainNow(tickMs: number = 1000, refreshMs: number = 30_000): number {
  const { connection } = useConnection();
  const offsetRef = useRef<number>(0);
  const [chainNow, setChainNow] = useState<number>(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    let cancelled = false;

    const refreshOffset = async (): Promise<void> => {
      try {
        const slot = await connection.getSlot("confirmed");
        const blockTime = await connection.getBlockTime(slot);
        if (cancelled || blockTime == null) return;
        offsetRef.current = blockTime - Math.floor(Date.now() / 1000);
        setChainNow(Math.floor(Date.now() / 1000) + offsetRef.current);
      } catch {
        // RPC blip — keep prior offset, next tick will still advance.
      }
    };

    void refreshOffset();
    const refreshId = setInterval(() => void refreshOffset(), refreshMs);
    const tickId = setInterval(() => {
      setChainNow(Math.floor(Date.now() / 1000) + offsetRef.current);
    }, tickMs);

    return (): void => {
      cancelled = true;
      clearInterval(refreshId);
      clearInterval(tickId);
    };
  }, [connection, tickMs, refreshMs]);

  return chainNow;
}
